import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { Duration } from 'luxon';
import { useIntl } from 'react-intl';
import { useFormState } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import {
  NOTE_FIELDS,
  useFormattedDuration,
  useTimerContext
} from 'modules/notes/common';
import { useMessages } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';
import {
  checkInputDuration,
  formatSecondsToHour,
  isDurationWithin24Hours
} from '../../../../utils/duration-util';

const useStyles = makeStyles((theme) => ({
  helpetText: {
    margin: theme.spacing(0)
  }
}));

type TimeDurationControlProps = {
  onEnter: (value: number | null) => void;
  running: boolean;
  timer: Duration;
  duration: any;
  testId: string;
  value?: string;
  resetClickedOnInvalidDuration?: boolean;
} & TextFieldProps;

const useDurationErrorProps = (error: boolean, errorText: string) => {
  const timeErrorProps = useMemo(() => {
    if (error) {
      return {
        error: true,
        helperText: errorText
      };
    } else return {};
  }, [error, errorText]);

  return timeErrorProps;
};

const messageIds = [
  'note.error.durationRequired',
  'note.error.invalidDuration',
  'note.error.maxDurationExceeded',
  'note.error.durationShouldBeInStartTimeEndTimeRange'
];

export const TimeDurationControl: React.FC<TimeDurationControlProps> = ({
  running,
  timer,
  disabled,
  duration,
  InputProps,
  testId,
  onEnter,
  resetClickedOnInvalidDuration = false,
  ...rest
}: TimeDurationControlProps) => {
  const formattedDuration = useFormattedDuration({
    duration: running ? timer.seconds : duration,
    longDurationFormat: running,
    needsLeadingZeroes: true
  });
  const { setDisableTimer } = useTimerContext();

  const displayDuration = useMemo(() => {
    return !running
      ? formatSecondsToHour(duration, true, true)
      : formattedDuration;
  }, [duration, formattedDuration, running]);

  const [durationText, setDurationText] = useState<string>(displayDuration);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [seconds, setSeconds] = useState(0);

  const classes = useStyles();
  useEffect(() => {
    if (duration !== null || resetClickedOnInvalidDuration) {
      const displayDuration = !running
        ? formatSecondsToHour(duration, true, true)
        : formattedDuration;
      setDurationText(displayDuration);

      if (!running) {
        setSeconds(duration);
        setDisableTimer(!isDurationWithin24Hours(duration, false));
        setIsValid(isDurationWithin24Hours(duration));
      } else {
        setSeconds(0);
        setDisableTimer(false);
        setIsValid(true);
      }
    }
  }, [
    duration,
    formattedDuration,
    resetClickedOnInvalidDuration,
    running,
    setDisableTimer
  ]);

  const handleChange = useCallback(
    (event: any) => {
      const { seconds, isValid } = checkInputDuration(
        event.target.value || '0'
      );

      setSeconds(seconds);
      setDisableTimer(!isValid || !isDurationWithin24Hours(seconds, false));
      setIsValid(isValid);
      setDurationText(event.target.value);
    },
    [setDisableTimer]
  );
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );
  const { errors } = useFormState();
  useEffect(() => {
    if (
      errors &&
      errors[NOTE_FIELDS.DURATION] &&
      isTiwDurationIndependentStartEndTime
    )
      setIsValid(false);
    else setIsValid(true);
  }, [errors, isTiwDurationIndependentStartEndTime]);

  const handleBlur = useCallback(
    (event: any) => {
      const { parsedSecond, isValid, seconds } = checkInputDuration(
        event.target.value || '0'
      );
      setSeconds(seconds);
      setDisableTimer(!isValid || !isDurationWithin24Hours(seconds, false));
      setIsValid(isValid);
      if (isValid) {
        const formattedValue = formatSecondsToHour(parsedSecond, true, true);
        const existingValue = formatSecondsToHour(duration, true, true);
        setDurationText(formattedValue);
        if (onEnter && formattedValue !== existingValue) {
          onEnter(parsedSecond);
        }
      } else {
        onEnter(null);
      }
    },
    [setDisableTimer, duration, onEnter]
  );

  const { formatMessage } = useIntl();

  const durationInputProps = useMemo(() => {
    return {
      'data-testid': testId,
      'aria-label': formatMessage({ id: 'note.timeDuration' })
    };
  }, [formatMessage, testId]);

  const messages = useMessages(messageIds);

  const errorMessage = useMemo(() => {
    if (!durationText.length) {
      return messages['note.error.durationRequired'];
    } else if (
      errors?.[NOTE_FIELDS.DURATION]?.message ===
        'note.error.durationShouldBeInStartTimeEndTimeRange' &&
      isTiwDurationIndependentStartEndTime
    ) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      messages[errors[NOTE_FIELDS.DURATION]?.message];
    }
    return !isDurationWithin24Hours(seconds)
      ? messages['note.error.maxDurationExceeded']
      : messages['note.error.invalidDuration'];
  }, [
    durationText.length,
    errors,
    isTiwDurationIndependentStartEndTime,
    messages,
    seconds
  ]);

  const timeErrorProps = useDurationErrorProps(!isValid, errorMessage);

  const handleFocus = useCallback((event: any) => {
    if (event.target.value === '00h:00m') {
      setDurationText('');
    } else {
      event.target.select();
    }
  }, []);

  return (
    <TextField
      type={'text'}
      variant="outlined"
      value={durationText}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      {...timeErrorProps}
      inputProps={durationInputProps}
      InputProps={InputProps}
      FormHelperTextProps={{
        className: classes.helpetText
      }}
      {...rest}
    />
  );
};
